import { useCallback } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { ApiService, CompanyDailyNavConfigUpdate, FirmDailyNavConfigUpdate } from 'api';
import { useResponse } from 'services/hooks/useResponse';

export const FIRM_DAILY_NAV_CONFIG_QUERY_KEY = 'FIRM_DAILY_NAV_CONFIG';
export const FIRM_DAILY_NAV_CONFIG_HISTORY_QUERY_KEY = 'FIRM_DAILY_NAV_CONFIG_HISTORY';
export const COMPANY_DAILY_NAV_CONFIG_QUERY_KEY = 'COMPANY_DAILY_NAV_CONFIG';
export const COMPANY_DAILY_NAV_CONFIG_HISTORY_QUERY_KEY = 'COMPANY_DAILY_NAV_CONFIG_HISTORY';
export const COMPANY_DAILY_NAV_GPC_APPROACHES_QUERY_KEY = 'COMPANY_DAILY_NAV_GPC_APPROACHES';

// DRF page-number pagination: pages are always sequential, so the next page
// number is just how many pages have already loaded, plus one.
const getNextHistoryPageParam = <T extends { next?: string | null }>(lastPage: T, allPages: T[]) =>
  lastPage.next ? allPages.length + 1 : undefined;

export const useGetFirmDailyNavConfig = (firmId?: number | null) => {
  const { data, isFetching, isLoading, refetch } = useQuery({
    enabled: !isNil(firmId),
    queryFn: () => ApiService.apiFirmsDailyNavConfigList(String(firmId)),
    queryKey: [FIRM_DAILY_NAV_CONFIG_QUERY_KEY, firmId],
  });

  return { data, isFetching, isLoading, refetch };
};

export const useGetFirmDailyNavConfigHistory = (firmId?: number | null) => {
  const { data, isFetching, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    enabled: !isNil(firmId),
    queryKey: [FIRM_DAILY_NAV_CONFIG_HISTORY_QUERY_KEY, firmId],
    queryFn: ({ pageParam }) => ApiService.apiFirmsDailyNavConfigHistoryList(String(firmId), pageParam),
    initialPageParam: 1,
    getNextPageParam: getNextHistoryPageParam,
  });

  return {
    history: data?.pages.flatMap(page => page.results),
    isFetching,
    isLoading,
    loadMoreHistory: fetchNextPage,
    hasMoreHistory: Boolean(hasNextPage),
    isLoadingMoreHistory: isFetchingNextPage,
  };
};

export const useUpdateFirmDailyNavConfig = () => {
  const queryClient = useQueryClient();
  const { processErrorResponse } = useResponse();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ firmId, data }: { firmId: number; data: FirmDailyNavConfigUpdate }) =>
      ApiService.apiFirmsDailyNavConfigUpdate(String(firmId), data),
  });

  const updateFirmDailyNavConfig = useCallback(
    async (firmId: number, data: FirmDailyNavConfigUpdate) => {
      try {
        const updatedConfig = await mutateAsync({ firmId, data });
        await queryClient.invalidateQueries({ queryKey: [FIRM_DAILY_NAV_CONFIG_QUERY_KEY, firmId] });
        await queryClient.invalidateQueries({ queryKey: [FIRM_DAILY_NAV_CONFIG_HISTORY_QUERY_KEY, firmId] });
        // A firm template change cascades a new config version to every company
        await queryClient.invalidateQueries({ queryKey: [COMPANY_DAILY_NAV_CONFIG_QUERY_KEY] });
        await queryClient.invalidateQueries({ queryKey: [COMPANY_DAILY_NAV_CONFIG_HISTORY_QUERY_KEY] });
        return updatedConfig;
      } catch (error) {
        processErrorResponse({
          error,
          defaultErrorMessage: 'An error occurred while saving the Daily NAV settings',
          action: 'save the Daily NAV settings',
        });
        return null;
      }
    },
    [mutateAsync, queryClient, processErrorResponse]
  );

  return { updateFirmDailyNavConfig, isPending };
};

export const useGetCompanyDailyNavConfig = (firmId?: number | null, companyId?: number | null) => {
  const { data, isFetching, isLoading, refetch } = useQuery({
    enabled: !isNil(firmId) && !isNil(companyId),
    queryFn: () => ApiService.apiFirmsDailyNavCompaniesConfigList(String(firmId), String(companyId)),
    queryKey: [COMPANY_DAILY_NAV_CONFIG_QUERY_KEY, firmId, companyId],
  });

  return { data, isFetching, isLoading, refetch };
};

export const useGetCompanyDailyNavConfigHistory = (firmId?: number | null, companyId?: number | null) => {
  const { data, isFetching, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    enabled: !isNil(firmId) && !isNil(companyId),
    queryKey: [COMPANY_DAILY_NAV_CONFIG_HISTORY_QUERY_KEY, firmId, companyId],
    queryFn: ({ pageParam }) =>
      ApiService.apiFirmsDailyNavCompaniesConfigHistoryList(String(firmId), String(companyId), pageParam),
    initialPageParam: 1,
    getNextPageParam: getNextHistoryPageParam,
  });

  return {
    history: data?.pages.flatMap(page => page.results),
    isFetching,
    isLoading,
    loadMoreHistory: fetchNextPage,
    hasMoreHistory: Boolean(hasNextPage),
    isLoadingMoreHistory: isFetchingNextPage,
  };
};

// The company's GPC approaches and their comps, offered up as the source list for the
// "copy from GPC" Daily NAV comps watchlist picker. This is a one-time copy, so no
// invalidation coupling to the config mutation is needed.
export const useGetCompanyGpcApproaches = (companyMeasurementDateId?: number | null) => {
  const { data, isLoading } = useQuery({
    enabled: !isNil(companyMeasurementDateId),
    queryFn: () =>
      ApiService.apiDailyNavCompanyMeasurementDatesGpcApproachesList(String(companyMeasurementDateId)),
    queryKey: [COMPANY_DAILY_NAV_GPC_APPROACHES_QUERY_KEY, companyMeasurementDateId],
  });

  return { approaches: data, isLoading };
};

export const useUpdateCompanyDailyNavConfig = () => {
  const queryClient = useQueryClient();
  const { processErrorResponse } = useResponse();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      firmId,
      companyId,
      data,
    }: {
      firmId: number;
      companyId: number;
      data: CompanyDailyNavConfigUpdate;
    }) => ApiService.apiFirmsDailyNavCompaniesConfigUpdate(String(firmId), String(companyId), data),
  });

  const updateCompanyDailyNavConfig = useCallback(
    async (firmId: number, companyId: number, data: CompanyDailyNavConfigUpdate) => {
      try {
        const updatedConfig = await mutateAsync({ firmId, companyId, data });
        await queryClient.invalidateQueries({ queryKey: [COMPANY_DAILY_NAV_CONFIG_QUERY_KEY, firmId, companyId] });
        await queryClient.invalidateQueries({
          queryKey: [COMPANY_DAILY_NAV_CONFIG_HISTORY_QUERY_KEY, firmId, companyId],
        });
        return updatedConfig;
      } catch (error) {
        processErrorResponse({
          error,
          defaultErrorMessage: 'An error occurred while saving the Daily Monitoring configuration',
          action: 'save the Daily Monitoring configuration',
        });
        return null;
      }
    },
    [mutateAsync, queryClient, processErrorResponse]
  );

  return { updateCompanyDailyNavConfig, isPending };
};
